
import { mdiPencil } from '@mdi/js'
import { mapGetters } from 'vuex'
import ModelSelectOptionForm from './ModelSelectOptionForm.vue'
import catalog from '@/components/tools/catalog'
import utils from '@/components/tools/utils'

const arrayParamDelimiter = ','

export default {
  mdiPencil,
  components: { ModelSelectOptionForm },
  props: {
    flat: {
      type: Boolean,
      default: false
    },
    accordion: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      panel: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      editingModelSelectOptionLabel: null,
      modelSearch: ''
    }
  },
  computed: {
    ...mapGetters('catalog', {
      filters: 'filters'
    }),

    ...mapGetters({ isAdmin: 'isUserAdmin' }),

    firstRegistrationOptions () {
      const maxYear = new Date().getFullYear()
      const minYear = 1990
      const options = [{ value: 0, label: this.$t('Any') }]

      for (let year = maxYear; year >= minYear; year--) {
        options.push({ value: year, label: year })
      }

      return options
    },

    selectedFilters () {
      const selectedFiltersEntries = Object.entries(this.filters).flatMap(
        ([k, { attributeCode, backendType }]) => {
          const values = this.$route.query[attributeCode]

          if (attributeCode === 'position_country_code' && values) {
            return [[k, values.split(arrayParamDelimiter)]]
          } else if (attributeCode === 'mileage') {
            const mileage = []
            const splits = values?.split(arrayParamDelimiter)
            if (splits?.length === 2) {
              mileage.push(['mileage_from', parseInt(splits[0])])
              mileage.push(['mileage_to', parseInt(splits[1])])

              return mileage
            } else {
              mileage.push(['mileage_from', undefined])
              mileage.push(['mileage_to', undefined])
              return mileage
            }
          } else if (attributeCode === 'first_registration') {
            const firstRegistration = []
            const splits = values?.split(arrayParamDelimiter)
            if (splits?.length === 2) {
              firstRegistration.push(['first_registration_from', parseInt(splits[0])])
              firstRegistration.push(['first_registration_to', parseInt(splits[1])])

              return firstRegistration
            } else {
              firstRegistration.push(['first_registration_from', undefined])
              firstRegistration.push(['first_registration_to', undefined])
              return firstRegistration
            }
          } else if (attributeCode === 'target_price') {
            const targetPrice = []
            const splits = values?.split(arrayParamDelimiter)
            if (splits?.length === 2) {
              targetPrice.push(['target_price_from', parseInt(splits[0])])
              targetPrice.push(['target_price_to', parseInt(splits[1])])

              return targetPrice
            } else {
              targetPrice.push(['target_price_from', undefined])
              targetPrice.push(['target_price_to', undefined])

              return targetPrice
            }
          }

          if (attributeCode === 'model_select') {
            return [[k, values?.split(arrayParamDelimiter)]]
          }

          const parsedValues =
            backendType === 'int' ? this.parseInts(values) : values

          return [[k, parsedValues]]
        }
      )

      // try {
      //   console.log(selectedFiltersEntries)
      return Object.fromEntries(selectedFiltersEntries)
      // } catch (e) {
      //   console.error(e)
      //   return {}
      // }
    },

    auctionStatusFilterOptions () {
      const options = []
      const completedStatusOption = { value: 'sold,failed', count: 0 }
      const excluded = ['editing', 'under_review', 'ignored_in_catalog', 'failed_na']

      this.filters.auction_status?.options.forEach((option) => {
        if (this.isAdmin === false) {
          excluded.push('published')
          excluded.push('renegotiation')
          excluded.push('acpt_late_offer')
          excluded.push('recd_late_offer')
        }

        if (excluded.includes(option.value)) {
          return
        }

        if (['sold', 'failed'].includes(option.value)) {
          completedStatusOption.count += option.count
          return
        }

        options.push(option)
      })

      if (completedStatusOption.count > 0) {
        options.push(completedStatusOption)
      }

      options.sort((a, b) => {
        if (a.value === 'running') {
          return -1
        }
        if (b.value === 'running') {
          return 1
        }

        if (a.value === 'recd_late_offer') {
          return 1
        }
        if (b.value === 'recd_late_offer') {
          return -1
        }
        if (a.value === 'acpt_late_offer') {
          return 1
        }
        if (b.value === 'acpt_late_offer') {
          return -1
        }

        return a.value.localeCompare(b.value)
      })

      return options
    },

    privateAuctionFilterOptions () {
      const options = this.filters.private_auction?.options
      if (options && options.length > 0) {
        return options.map((option) => {
          return {
            ...option,
            label: this.$t(option.label)
          }
        })
      } else {
        return null
      }
    },

    buyNowFilterOptions () {
      const options = this.filters.buy_now?.options.filter(option => option.value === 'true') ?? []

      // TODO: rewrite this ugly hack
      if (options.length > 0) {
        return options.map((option) => {
          return {
            ...option,
            label: this.$t(option.label)
          }
        })
      }

      return [{
        label:
          this.$t('buy_now_available'),
        count: 0,
        value: 'true',
        sortOrder: 1,
        optionId: null
      }]
    },

    distanceFilterOptions () {
      if (this.selectedFilters.distance === undefined) {
        return this.filters.distance?.options
      }
      return this.filters.distance?.options.filter(
        option => option.value === this.selectedFilters.distance
      )
    },

    positionCountryCodeFilterOptions () {
      return this.filters.position_country_code?.options.filter(
        option => option.count > 0 || this.selectedFilters.position_country_code?.includes(option.value)
      ).sort((a, b) => {
        return this.getLocationLabel(a.value).localeCompare(this.getLocationLabel(b.value))
      })
    },
    mileageOptions () {
      return [
        0, 5, 50, 1000, 5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000,
        80000, 90000, 100000, 150000, 200000
      ].map((value) => {
        if (value > 0) {
          return {
            value,
            label: this.getMileageLabel(value)
          }
        } else {
          return {
            value: 0,
            label: this.$t('Any')
          }
        }
      })
    },
    targetPriceOptions () {
      return [
        0, 10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000,
        100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000
      ].map((value) => {
        const text = value > 0 ? utils.formatCurrency(value) : this.$t('Any')

        return { value, text }
      })
    },
    modelSelectOptions () {
      const filterValues = this.selectedFilters.model_select ?? []
      const definedOptions = [] // this.filters.model_select?.options ?? []
      const customOptions = filterValues
        .filter(label =>
          definedOptions.every(option => option.label !== label)
        )
        .map(label => ({ label, count: null }))

      return [...customOptions, ...definedOptions]
    }
  },
  methods: {
    parseInts (values) {
      if (typeof values !== 'string') {
        return
      }

      return values.split(arrayParamDelimiter).map(value => parseInt(value))
    },

    getAuctionStatusLabel (value) {
      return catalog.getAuctionStatusLabel(value, false, this)
    },

    getLocationLabel (value) {
      return this.$t(`catalog.location.country.${value}`)
    },

    getMileageLabel (value) {
      return utils.formatNumber(value) + ' km'
    },

    onFilterChange (key, value) {
      const query = { ...this.$route.query }

      delete query.page
      if (key === 'mileage_from' || key === 'mileage_to') {
        const old = query.mileage
        // exists
        let mileageFrom = 0
        let mileageTo = 0
        if (old && old.split(',')?.length === 2) {
          const splits = old.split(',')
          mileageFrom = parseInt(splits[0])
          mileageTo = parseInt(splits[1])
        }
        if (key === 'mileage_from') {
          mileageFrom = value || 0
        }
        if (key === 'mileage_to') {
          mileageTo = value || 0
        }

        delete query.mileage
        if (mileageFrom + mileageTo > 0) {
          query.mileage = `${mileageFrom},${mileageTo}`
        }
      } else if (key === 'first_registration_from' || key === 'first_registration_to') {
        const old = query.first_registration
        // exists
        let firstRegistrationFrom = 0
        let firstRegistrationTo = 0
        if (old && old.split(',')?.length === 2) {
          const splits = old.split(',')
          firstRegistrationFrom = parseInt(splits[0])
          firstRegistrationTo = parseInt(splits[1])
        }
        if (key === 'first_registration_from') {
          firstRegistrationFrom = value || 0
        }
        if (key === 'first_registration_to') {
          firstRegistrationTo = value || 0
        }

        delete query.first_registration
        if (firstRegistrationFrom + firstRegistrationTo > 0) {
          query.first_registration = `${firstRegistrationFrom},${firstRegistrationTo}`
        }
      } else if (key === 'target_price_from' || key === 'target_price_to') {
        const old = query.target_price
        let targetPriceFrom = 0
        let targetPriceTo = 0

        if (old && old.split(',')?.length === 2) {
          const splits = old.split(',')
          targetPriceFrom = parseInt(splits[0])
          targetPriceTo = parseInt(splits[1])
        }

        if (key === 'target_price_from') {
          targetPriceFrom = value || 0
        }

        if (key === 'target_price_to') {
          targetPriceTo = value || 0
        }

        delete query.target_price

        if (targetPriceFrom + targetPriceTo > 0) {
          query.target_price = `${targetPriceFrom},${targetPriceTo}`
        }
      } else {
        delete query[key]
        const parsedValue =
          value instanceof Array
            ? value.join(arrayParamDelimiter)
            : value

        if (parsedValue) {
          query[key] = parsedValue
        }
      }

      this.$router.push({ query })

      if (key === 'model_select') {
        this.$refs.modelSelectCombobox.blur()
      }
    },

    setEditingModelSelectOptionLabel (label) {
      this.editingModelSelectOptionLabel = label
    },

    updateModelSelectOptionLabel (currentLabel, newLabel) {
      this.editingModelSelectOptionLabel = null

      if (currentLabel === newLabel) {
        return
      }

      const newModelSelectFilterValues = this.selectedFilters.model_select.map(
        value => (value === currentLabel ? newLabel : value)
      )

      this.onFilterChange('model_select', newModelSelectFilterValues)
    },

    addModelSelectOption (label) {
      // console.log('Adding model select option:', label)
      const trimmed = label.trim()
      if (!trimmed) {
        return
      }

      const current = this.selectedFilters.model_select ?? []
      const newValues = [...new Set([...current, trimmed])]
      this.onFilterChange('model_select', newValues)
      this.modelSearch = ''

      this.$refs.modelSelectCombobox.blur()
    }
  }
}

